import { mapFormDataToDto } from 'components/modals/expenses/ExpenseModal';
import { ClassifierData } from 'types/classifiers';
import { ExpenseCreateDto, ExpenseDetails, ExpenseForm } from 'types/expenses';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';

/** The classifiers {@link expenseDtoToForm} reads. */
export const EXPENSE_FORM_CLASSIFIERS = ['EXPENSE_TYPE', 'CURRENCY'];

type Option = { label: string; value: string };

/** The expense form of a stored expense. The amount is the one in the original currency, as entered. */
export const expenseDtoToForm = (expense: ExpenseDetails, classifiers: Record<string, ClassifierData>): ExpenseForm => {
    const typeOptions = getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as Option[];
    const currencyOptions = getOptionsFromClassifier(classifiers['CURRENCY']) as Option[];

    return {
        id: expense.id,
        externalId: expense.externalId ?? null,
        expenseType: typeOptions.find((option) => option.value === expense.expenseTypeCode) ?? null,
        currencyType: currencyOptions.find((option) => option.value === expense.originalCurrencyTypeCode) ?? null,
        description: expense.description ?? '',
        expenseAmount: expense.expenseAmountOriginalCurrency?.toString() ?? '',
        expenseDate: expense.expenseDate ? formatDateStringInput(new Date(expense.expenseDate)) : null,
        isNonBillable: expense.isNonBillable ?? false,
        employee: expense.employee ?? null,
        matter: expense.matter
            ? {
                  id: expense.matter.id,
                  name: expense.matter.name,
                  fullName: expense.matter.fullName,
                  isNonBillable: expense.matter.isNonBillable,
                  isConfidential: expense.matter.isConfidential,
                  visibility: expense.matter.visibility,
              }
            : null,
        effortId: expense.effortId ?? null,
        timecardId: expense.timecardId ?? null,
        createdAt: expense.createdAt,
        createdByEmployee: expense.createdByEmployee,
        lastModifiedAt: expense.lastModifiedAt,
        changedByEmployee: expense.changedByEmployee,
        invoices: expense.invoices,
    };
};

/** The create or update request of the expense form; the effort and timecard links go back as loaded. */
export const expenseFormToRequest = (form: ExpenseForm): ExpenseCreateDto => mapFormDataToDto(form);

/** The form fields each section of the expense page edits. */
export const EXPENSE_SECTION_FIELDS = {
    expense: ['expenseType', 'expenseAmount', 'currencyType', 'expenseDate', 'description'],
    booking: ['matter', 'employee', 'isNonBillable'],
} satisfies Record<string, (keyof ExpenseForm)[]>;

export type ExpenseSectionId = keyof typeof EXPENSE_SECTION_FIELDS;
